import React, { useMemo } from 'react';
import styled from 'styled-components';

export type OverflowTitleVariant = 'case-card' | 'case-table' | 'document-card' | 'document-list';

type IntrinsicTag = keyof React.JSX.IntrinsicElements;

interface VariantConfig {
  baseFontSize: number;
  compactFontSize: number;
  maxLines: number;
  fontWeight: number;
  wrapperDisplay: 'block' | 'inline-flex';
  alignItems: 'flex-start' | 'center';
  gap: number;
  defaultElement: IntrinsicTag;
}

const VARIANT_CONFIG: Record<OverflowTitleVariant, VariantConfig> = {
  'case-card': {
    baseFontSize: 17,
    compactFontSize: 15,
    maxLines: 2,
    fontWeight: 600,
    wrapperDisplay: 'block',
    alignItems: 'flex-start',
    gap: 0,
    defaultElement: 'h2',
  },
  'case-table': {
    baseFontSize: 14,
    compactFontSize: 13,
    maxLines: 2,
    fontWeight: 600,
    wrapperDisplay: 'block',
    alignItems: 'flex-start',
    gap: 0,
    defaultElement: 'span',
  },
  'document-card': {
    baseFontSize: 16,
    compactFontSize: 15,
    maxLines: 1,
    fontWeight: 600,
    wrapperDisplay: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    defaultElement: 'h3',
  },
  'document-list': {
    baseFontSize: 12,
    compactFontSize: 11,
    maxLines: 6,
    fontWeight: 500,
    wrapperDisplay: 'block',
    alignItems: 'flex-start',
    gap: 8,
    defaultElement: 'h3',
  },
};

const Wrapper = styled.span<{
  $display: VariantConfig['wrapperDisplay'];
  $align: VariantConfig['alignItems'];
  $gap: number;
}>`
  display: ${({ $display }) => $display};
  align-items: ${({ $align }) => $align};
  gap: ${({ $gap }) => `${$gap}px`};
  min-width: 0;
  max-width: 100%;
  flex: 1 1 auto;
`;

const TitleText = styled.span<{
  $fontSize: number;
  $fontWeight: number;
  $lineClamp: number;
}>`
  font-size: ${({ $fontSize }) => `${$fontSize}px`};
  font-weight: ${({ $fontWeight }) => $fontWeight};
  line-height: 1.3;
  display: ${({ $lineClamp }) => ($lineClamp > 1 ? '-webkit-box' : 'inline-block')};
  -webkit-box-orient: vertical;
  -webkit-line-clamp: ${({ $lineClamp }) => $lineClamp};
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;
  max-width: 100%;
  min-width: 0;
  flex: 1 1 auto;
  white-space: ${({ $lineClamp }) => ($lineClamp === 1 ? 'nowrap' : 'normal')};
  text-align: left;
  margin: 0;
`;

export interface OverflowTitleProps {
  title: string;
  variant: OverflowTitleVariant;
  as?: IntrinsicTag;
  children?: React.ReactNode;
  className?: string;
  tooltip?: boolean;
}

const hasLongToken = (value: string, threshold = 18): boolean => {
  const tokens = value.split(/\s+/).filter(Boolean);
  return tokens.some(token => token.length > threshold);
};

export const OverflowTitle: React.FC<OverflowTitleProps> = ({
  title,
  variant,
  as,
  children,
  className,
  tooltip = true,
}) => {
  const config = VARIANT_CONFIG[variant];
  const hasLongWord = useMemo(() => hasLongToken(title), [title]);
  const fontSize = hasLongWord ? config.compactFontSize : config.baseFontSize;
  const element = as ?? config.defaultElement;
  const dynamicProps = useMemo(
    () => ({
      as: element as React.ElementType,
      $fontSize: fontSize,
      $fontWeight: config.fontWeight,
      $lineClamp: config.maxLines,
      title: tooltip ? title : undefined,
    }),
    [element, fontSize, config.fontWeight, config.maxLines, title, tooltip],
  );

  return (
    <Wrapper className={className} $display={config.wrapperDisplay} $align={config.alignItems} $gap={config.gap}>
      <TitleText {...dynamicProps}>
        {title}
      </TitleText>
      {children}
    </Wrapper>
  );
};

export default OverflowTitle;
